import React, { useState, useEffect } from "react";
import { Box, FormControl, InputLabel, Select, MenuItem, Typography, Chip, Alert } from "@mui/material";
import { createLogger } from "../utils/logger";

const logger = createLogger("TimezoneSelector");

// Major timezones - we'll calculate current offsets dynamically
const MAJOR_TIMEZONES = [
  { value: "Pacific/Honolulu", label: "Hawaii Time" },
  { value: "America/Anchorage", label: "Alaska Time" },
  { value: "America/Los_Angeles", label: "Pacific Time" },
  { value: "America/Phoenix", label: "Arizona Time" },
  { value: "America/Denver", label: "Mountain Time" },
  { value: "America/Chicago", label: "Central Time" },
  { value: "America/New_York", label: "Eastern Time" },
  { value: "UTC", label: "UTC" },
  { value: "Europe/London", label: "London" },
  { value: "Europe/Paris", label: "Paris" },
  { value: "Europe/Berlin", label: "Berlin" },
  { value: "Europe/Rome", label: "Rome" },
  { value: "Europe/Madrid", label: "Madrid" },
  { value: "Europe/Amsterdam", label: "Amsterdam" },
  { value: "Europe/Stockholm", label: "Stockholm" },
  { value: "Europe/Zurich", label: "Zurich" },
  { value: "Europe/Vienna", label: "Vienna" },
  { value: "Europe/Prague", label: "Prague" },
  { value: "Europe/Warsaw", label: "Warsaw" },
  { value: "Europe/Athens", label: "Athens" },
  { value: "Europe/Istanbul", label: "Istanbul" },
  { value: "Europe/Moscow", label: "Moscow" },
  { value: "Asia/Dubai", label: "Dubai" },
  { value: "Asia/Karachi", label: "Karachi" },
  { value: "Asia/Kolkata", label: "Kolkata" },
  { value: "Asia/Dhaka", label: "Dhaka" },
  { value: "Asia/Bangkok", label: "Bangkok" },
  { value: "Asia/Shanghai", label: "Shanghai" },
  { value: "Asia/Hong_Kong", label: "Hong Kong" },
  { value: "Asia/Singapore", label: "Singapore" },
  { value: "Australia/Perth", label: "Perth" },
  { value: "Asia/Tokyo", label: "Tokyo" },
  { value: "Asia/Seoul", label: "Seoul" },
  { value: "Australia/Sydney", label: "Sydney" },
  { value: "Australia/Melbourne", label: "Melbourne" },
  { value: "Pacific/Auckland", label: "Auckland" },
  { value: "Pacific/Fiji", label: "Fiji" },
];

interface TimezoneSelectorProps {
  value?: string;
  onChange: (timezone: string) => void;
  autoDetect?: boolean;
  label?: string;
  fullWidth?: boolean;
  error?: string;
  helperText?: string;
}

export const TimezoneSelector: React.FC<TimezoneSelectorProps> = ({ value = "", onChange, autoDetect = true, label = "Timezone", fullWidth = true, error, helperText }) => {
  const [detectedTimezone, setDetectedTimezone] = useState<string>("");
  const [showAutoDetect, setShowAutoDetect] = useState(false);

  useEffect(() => {
    if (autoDetect) {
      try {
        const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        setDetectedTimezone(browserTimezone);

        // If no value is set, auto-select the detected timezone
        if (!value) {
          onChange(browserTimezone);
        }
      } catch (error) {
        logger.warn("Failed to detect browser timezone:", error);
      }
    }
  }, [autoDetect, value, onChange]);

  const handleTimezoneChange = (event: any) => {
    const selectedTimezone = event.target.value;
    onChange(selectedTimezone);
  };

  const handleAutoDetect = () => {
    if (detectedTimezone) {
      onChange(detectedTimezone);
      setShowAutoDetect(false);
    }
  };

  const getCurrentTimezoneOffset = (timezone: string) => {
    try {
      const now = new Date();

      // Get the timezone offset using a more reliable method
      const utcTime = new Date(now.toLocaleString("en-US", { timeZone: "UTC" }));
      const targetTime = new Date(now.toLocaleString("en-US", { timeZone: timezone }));

      // Calculate the difference in hours
      const offsetInMinutes = (targetTime.getTime() - utcTime.getTime()) / (1000 * 60);
      const offsetInHours = offsetInMinutes / 60;

      // Round to nearest 0.5 hour (for timezones like India which are +5.5)
      return Math.round(offsetInHours * 2) / 2;
    } catch (error) {
      return 0;
    }
  };

  const getTimezoneOffsetString = (offset: number) => {
    if (offset === 0) return "UTC";
    if (offset > 0) {
      // Handle decimal offsets like +5.5 for India
      if (offset % 1 === 0.5) {
        return `UTC+${Math.floor(offset)}:30`;
      }
      return `UTC+${offset}`;
    }
    // Handle negative offsets
    if (offset % 1 === -0.5) {
      return `UTC${Math.ceil(offset)}:30`;
    }
    return `UTC${offset}`;
  };

  // Calculate current offsets and sort timezones
  const timezonesWithCurrentOffsets = MAJOR_TIMEZONES.map((tz) => ({
    ...tz,
    currentOffset: getCurrentTimezoneOffset(tz.value),
  })).sort((a, b) => a.currentOffset - b.currentOffset);

  return (
    <Box>
      <FormControl fullWidth={fullWidth} error={!!error}>
        <InputLabel id="timezone-select-label">{label}</InputLabel>
        <Select labelId="timezone-select-label" value={value} onChange={handleTimezoneChange} label={label}>
          {autoDetect && detectedTimezone && !value && (
            <MenuItem value="">
              <Box>
                <Typography variant="body2" color="primary">
                  Auto-detected: {detectedTimezone}
                </Typography>
              </Box>
            </MenuItem>
          )}
          {timezonesWithCurrentOffsets.map((tz) => (
            <MenuItem key={tz.value} value={tz.value}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2">{tz.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {getTimezoneOffsetString(tz.currentOffset)}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {autoDetect && detectedTimezone && !value && (
        <Box sx={{ mt: 1 }}>
          <Alert severity="info" sx={{ py: 1 }}>
            <Typography variant="body2">
              We detected your timezone as <strong>{detectedTimezone}</strong>. You can change this in the dropdown above.
            </Typography>
          </Alert>
        </Box>
      )}

      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
          {helperText}
        </Typography>
      )}

      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};
